import {
  MAX_TEAM_ID_LENGTH,
  MAX_TEMPLATE_NAME_LENGTH,
  MAX_TEMPLATE_SQL_LENGTH,
  TEMPLATE_COLUMN_FUNCTION_REQUIRED,
  TEMPLATE_COLUMNS_REQUIRED,
  TEMPLATE_DATE_FIELD_REQUIRED,
  TEMPLATE_DATE_PERIOD_REQUIRED,
  TEMPLATE_MEASURE_REQUIRED,
  TEMPLATE_NAME_REQUIRED,
  TEMPLATE_NAME_TOO_LONG,
  TEMPLATE_NON_TIME_COLUMN_REQUIRED,
  TEMPLATE_QUERY_REQUIRED,
  TEMPLATE_QUERY_TOO_LONG,
  TEMPLATE_TEAM_INVALID,
  TEMPLATE_TEAM_REQUIRED,
  TEMPLATE_TIME_COLUMN_REQUIRED,
  TEMPLATE_TIME_GRANULARITY_REQUIRED,
} from "./definitions";
import {
  AggregationFunction,
  type ExportFormat,
  type ExportTemplateFields,
  type FilterSpec,
  type OutputColumn,
  type ReportExportConfig,
  type ReportTemplateFields,
  SplitPeriod,
  type SqlTemplateFields,
  type TemplateFields,
  TemplateType,
  type TimeGranularity,
} from "services/adi/types";

/**
 * Normalizes a team ID for use in ADI API calls.
 * Converts to lowercase, replaces spaces and special characters with hyphens.
 */
export const normalizeTeamId = (teamId: string | undefined | null): string => {
  if (!teamId) {
    return "";
  }
  return teamId.toLowerCase().replace(/[^a-z0-9-]/g, "-");
};

/**
 * Bounds ADI accepts when splitting export/report output files by line count.
 * Below the minimum the split produces an unmanageable number of files; above
 * the maximum the individual files become too large to open.
 */
export const MIN_LINES_PER_FILE = 10_000;
export const MAX_LINES_PER_FILE = 1_000_000;

/**
 * Upper bound quoted to users when sizing a line count, so a dropped digit is
 * recognisable as producing an unreasonable number of files.
 *
 * UI guidance only — no row cap is enforced anywhere in AuditsDataInsights. The
 * largest export observed in the Spark job's own fixtures is ~208M rows.
 */
export const MAX_EXPORT_ROWS = 500_000_000;

/**
 * Lines per file applied when a request omits a line count.
 *
 * Enforced twice: ExportMapper.DEFAULT_LINES_PER_FILE fills it in for exports
 * created through the API, and the Spark job's SplitConfig substitutes it for
 * any absent, empty, or non-positive value. The second path is what gives
 * reports the same default — the report state machine writes linesPerFile
 * straight from exportConfig.splitByLineCount, which is an empty string when
 * unset.
 *
 * A valid positive value is always honoured; only unusable values are replaced.
 */
export const DEFAULT_LINES_PER_FILE = 1_000_000;

/**
 * Guidance shown under the "Splitting options" header on both the export and
 * report wizards. Scoped to the section rather than to either checkbox: the
 * default applies even when file splitting is switched off entirely.
 */
export const splittingOptionsDescription = (): string =>
  `Exports are limited to ${(MAX_EXPORT_ROWS / 1_000_000).toLocaleString()} million rows. ` +
  `Lines per file defaults to ${DEFAULT_LINES_PER_FILE.toLocaleString()}.`;

/**
 * Validates a lines-per-file value taken from a text input.
 *
 * @param value raw input value
 * @returns an error message, or "" when the value is acceptable
 */
export const validateLinesPerFile = (value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) {
    return "Enter the number of lines per file.";
  }
  if (!/^\d+$/.test(trimmed)) {
    return "Lines per file must be a whole number.";
  }
  const parsed = Number(trimmed);
  if (parsed < MIN_LINES_PER_FILE || parsed > MAX_LINES_PER_FILE) {
    return `Lines per file must be between ${MIN_LINES_PER_FILE.toLocaleString()} and ${MAX_LINES_PER_FILE.toLocaleString()}.`;
  }
  return "";
};

/**
 * Builds the ordered output columns for a report.
 *
 * `timeColumn` is force-included as an ATTRIBUTE group-by dimension when the user
 * has not selected it, because the API requires it and because the report's own
 * export splits its files on it.
 *
 * That second reason is why the split column must be the time column and nothing
 * else. The report state machine writes the EXPORT# record straight from Step
 * Functions and never sets a column list, so the export job reads the report
 * table's columns as-is; a split column absent from that table fails the export
 * with "splitDateColumn '<name>' is not present in the exported columns". Adding
 * some other date column here would fix that but make it a second GROUP BY key,
 * changing the report's rows and how its measures divide.
 *
 * A column the user already selected keeps its chosen aggregation and position.
 */
export const buildReportOutputColumns = ({
  selectedColumns,
  aggregations,
  timeColumn,
}: {
  selectedColumns: string[];
  aggregations: Record<string, AggregationFunction>;
  timeColumn: string;
}): OutputColumn[] => {
  const cols: OutputColumn[] = selectedColumns.map((name) => ({
    columnName: name,
    // The time column is always a dimension, never a measure. A measure is
    // aliased "<name>_<fn>" in the report output, which would rename the very
    // column the export splits on and fail the export.
    function:
      name === timeColumn
        ? AggregationFunction.ATTRIBUTE
        : aggregations[name] || AggregationFunction.ATTRIBUTE,
  }));

  if (timeColumn && !cols.some((c) => c.columnName === timeColumn)) {
    cols.unshift({
      columnName: timeColumn,
      function: AggregationFunction.ATTRIBUTE,
    });
  }

  return cols;
};

export const validateTemplateName = (name: string): string => {
  const trimmed = name.trim();
  if (!trimmed) {
    return TEMPLATE_NAME_REQUIRED;
  }
  return trimmed.length > MAX_TEMPLATE_NAME_LENGTH
    ? TEMPLATE_NAME_TOO_LONG
    : "";
};

/**
 * Builds the saved export configuration. Per-run values (export name, the selected
 * months, file prefix) are excluded: a template is the shape of the job, not one run.
 */
export const buildExportTemplateFields = ({
  selectedColumns,
  format,
  dateFormat,
  splitEnabled,
  splitPeriod,
  splitDateColumn,
  linesPerFile,
}: {
  selectedColumns: string[];
  format?: ExportFormat;
  dateFormat?: string;
  splitEnabled: boolean;
  splitPeriod?: SplitPeriod;
  splitDateColumn?: string;
  linesPerFile?: string;
}): ExportTemplateFields => {
  const parsedLines = linesPerFile ? parseInt(linesPerFile, 10) : NaN;

  return {
    columns: selectedColumns,
    ...(format ? { format } : {}),
    ...(dateFormat ? { dateFormat } : {}),
    splitConfig: splitEnabled
      ? {
          splitBy: splitPeriod || SplitPeriod.MONTH,
          ...(splitDateColumn ? { splitDateColumn } : {}),
          ...(Number.isFinite(parsedLines)
            ? { linesPerFile: parsedLines }
            : {}),
        }
      : { splitBy: SplitPeriod.NONE },
  };
};

export const buildReportTemplateFields = ({
  columns,
  timeColumn,
  timeGranularity,
  exportConfig,
  filters,
}: {
  columns: OutputColumn[];
  timeColumn: string;
  timeGranularity: TimeGranularity;
  exportConfig: ReportExportConfig;
  filters?: FilterSpec;
}): ReportTemplateFields => {
  const hasFilters = filters
    ? Object.values(filters).some(
        (value) => value !== undefined && value !== "",
      )
    : false;

  return {
    columns,
    timeColumn,
    timeGranularity,
    exportConfig,
    ...(hasFilters && filters ? { filters } : {}),
  };
};

/** The handler rejects a payload carrying both members, so only one is ever set. */
export const buildTemplateFields = (
  templateType: TemplateType,
  fields: ExportTemplateFields | ReportTemplateFields | SqlTemplateFields,
): TemplateFields => {
  switch (templateType) {
    case TemplateType.EXPORT:
      return { exportFields: fields as ExportTemplateFields };
    case TemplateType.REPORT:
      return { reportFields: fields as ReportTemplateFields };
    case TemplateType.SQL:
      return { sqlFields: fields as SqlTemplateFields };
  }
};

export interface AppliedExportTemplate {
  selectedColumns: string[];
  format?: ExportFormat;
  dateFormat?: string;
  splitEnabled: boolean;
  splitPeriod?: SplitPeriod;
  splitDateColumn?: string;
  linesPerFile?: string;
  /** Saved columns the dataset's table no longer has, surfaced rather than dropped. */
  missingColumns: string[];
}

/**
 * The inverse of buildExportTemplateFields: maps a saved configuration back onto the
 * wizard's state. A template outlives the table it was built from, so any column that
 * has since disappeared is collected instead of silently discarded.
 */
export const applyExportTemplateFields = (
  fields: ExportTemplateFields,
  availableColumns: string[],
): AppliedExportTemplate => {
  const available = new Set(availableColumns);
  const missingColumns = fields.columns.filter((name) => !available.has(name));

  const splitBy = fields.splitConfig?.splitBy;
  const splitEnabled = Boolean(splitBy) && splitBy !== SplitPeriod.NONE;

  const savedSplitColumn = fields.splitConfig?.splitDateColumn;
  const splitColumnMissing = Boolean(
    savedSplitColumn && !available.has(savedSplitColumn),
  );
  if (
    splitColumnMissing &&
    savedSplitColumn &&
    !missingColumns.includes(savedSplitColumn)
  ) {
    missingColumns.push(savedSplitColumn);
  }

  // Mirrors ExportMapper's own rule (linesPerFile != null && > 0). The service
  // serialises an absent value as null rather than omitting it, so a bare
  // undefined check would read "no line split" as "split, value unknown" and
  // switch the control on with an empty box.
  const savedLines = fields.splitConfig?.linesPerFile;
  const linesPerFile =
    savedLines !== undefined && savedLines !== null && savedLines > 0
      ? savedLines
      : undefined;

  return {
    selectedColumns: fields.columns.filter((name) => available.has(name)),
    ...(fields.format ? { format: fields.format } : {}),
    ...(fields.dateFormat ? { dateFormat: fields.dateFormat } : {}),
    splitEnabled,
    ...(splitEnabled && splitBy ? { splitPeriod: splitBy } : {}),
    ...(savedSplitColumn && !splitColumnMissing
      ? { splitDateColumn: savedSplitColumn }
      : {}),
    ...(linesPerFile !== undefined
      ? { linesPerFile: String(linesPerFile) }
      : {}),
    missingColumns,
  };
};

export interface AppliedReportTemplate {
  columns: OutputColumn[];
  timeColumn: string;
  timeGranularity: TimeGranularity;
  exportConfig: ReportExportConfig;
  filters?: FilterSpec;
  missingColumns: string[];
}

/** The inverse of buildReportTemplateFields. See applyExportTemplateFields. */
export const applyReportTemplateFields = (
  fields: ReportTemplateFields,
  availableColumns: string[],
): AppliedReportTemplate => {
  const available = new Set(availableColumns);
  const missingColumns = fields.columns
    .filter((column) => !available.has(column.columnName))
    .map((column) => column.columnName);

  const timeColumnMissing = !available.has(fields.timeColumn);
  // buildReportOutputColumns force-includes the time column, so it is usually
  // already in fields.columns and would otherwise be listed twice.
  if (timeColumnMissing && !missingColumns.includes(fields.timeColumn)) {
    missingColumns.push(fields.timeColumn);
  }

  return {
    columns: fields.columns.filter((column) =>
      available.has(column.columnName),
    ),
    // Blanked rather than kept, so the wizard's own validation forces a new choice.
    timeColumn: timeColumnMissing ? "" : fields.timeColumn,
    timeGranularity: fields.timeGranularity,
    exportConfig: fields.exportConfig,
    ...(fields.filters ? { filters: fields.filters } : {}),
    missingColumns,
  };
};

// normalizeTeamId already replaces every character the model disallows, so only
// emptiness and length can fail here.
const validateTeamId = (teamId: string): string => {
  if (!teamId) {
    return TEMPLATE_TEAM_REQUIRED;
  }
  return teamId.length > MAX_TEAM_ID_LENGTH ? TEMPLATE_TEAM_INVALID : "";
};

/** Mirrors the model: ExportTemplateFields.columns is the only required member. */
export const validateExportTemplate = ({
  teamId,
  selectedColumns,
  autoAddedColumn,
}: {
  teamId: string;
  selectedColumns: string[];
  /** Split column the wizard added itself, which is not a user choice. */
  autoAddedColumn?: string;
}): string => {
  const teamError = validateTeamId(teamId);
  if (teamError) {
    return teamError;
  }
  const chosen = autoAddedColumn
    ? selectedColumns.filter((name) => name !== autoAddedColumn)
    : selectedColumns;

  return chosen.length === 0 ? TEMPLATE_COLUMNS_REQUIRED : "";
};

/** Mirrors the model: SqlTemplateFields.sql is required, @length(1, 10000). */
export const validateSqlTemplate = ({
  teamId,
  queryText,
}: {
  teamId: string;
  queryText: string;
}): string => {
  const teamError = validateTeamId(teamId);
  if (teamError) {
    return teamError;
  }
  if (!queryText.trim()) {
    return TEMPLATE_QUERY_REQUIRED;
  }
  // The model counts the text as saved, so measure it untrimmed.
  return queryText.length > MAX_TEMPLATE_SQL_LENGTH
    ? TEMPLATE_QUERY_TOO_LONG
    : "";
};

/**
 * Mirrors the model: ReportTemplateFields requires columns, timeColumn,
 * timeGranularity and an exportConfig carrying datePeriod and dateField. Every
 * OutputColumn needs both a name and a function.
 */
export const validateReportTemplate = ({
  teamId,
  columns,
  timeColumn,
  timeGranularity,
  datePeriod,
  dateField,
}: {
  teamId: string;
  columns: OutputColumn[];
  timeColumn: string;
  timeGranularity?: TimeGranularity;
  datePeriod?: TimeGranularity;
  dateField: string;
}): string => {
  const teamError = validateTeamId(teamId);
  if (teamError) {
    return teamError;
  }
  if (columns.length === 0) {
    return TEMPLATE_COLUMNS_REQUIRED;
  }
  if (columns.some((column) => !column.columnName || !column.function)) {
    return TEMPLATE_COLUMN_FUNCTION_REQUIRED;
  }
  if (!timeColumn) {
    return TEMPLATE_TIME_COLUMN_REQUIRED;
  }
  if (columns.every((column) => column.columnName === timeColumn)) {
    return TEMPLATE_NON_TIME_COLUMN_REQUIRED;
  }
  if (
    !columns.some((column) => column.function !== AggregationFunction.ATTRIBUTE)
  ) {
    return TEMPLATE_MEASURE_REQUIRED;
  }
  if (!timeGranularity) {
    return TEMPLATE_TIME_GRANULARITY_REQUIRED;
  }
  if (!datePeriod) {
    return TEMPLATE_DATE_PERIOD_REQUIRED;
  }
  if (!dateField) {
    return TEMPLATE_DATE_FIELD_REQUIRED;
  }
  return "";
};
